/**
 * Notifications types — mirrors `notification_remote_datasource.dart` and
 * `notification_rules_datasource.dart`.
 */

export type InAppNotification = {
  id: string
  title: string
  body: string
  type: number
  category: string | null
  referenceId: string | null
  referenceType: string | null
  actionUrl: string | null
  isRead: boolean
  createdAt: string
  channels: number
}

export type InAppNotificationListParams = {
  page?: number
  pageSize?: number
}

export type InAppNotificationListResult = {
  items: InAppNotification[]
  totalCount: number
}

export type NotificationRule = {
  id: string
  eventType: number
  audienceRole: string
  isEnabled: boolean
  enabledChannels: number
  stageFilter: string | null
  clientFilter: string | null
  quietHoursStart: string | null
  quietHoursEnd: string | null
  digestMode: number
}

export type NotificationTemplate = {
  id: string
  eventType: number
  audienceRole: string
  channel: number
  subjectTemplate: string | null
  bodyTemplate: string
  isCustom: boolean
}

export type NotificationTemplatePreview = {
  subject: string
  body: string
}

export type UserNotificationPreference = {
  eventType: number
  mutedChannels: number
  tenantAllowedChannels: number
  effectiveChannels: number
}

function readStr(m: Record<string, unknown>, camel: string, pascal: string): string {
  const v = m[camel] ?? m[pascal]
  if (v == null) return ''
  return String(v).trim()
}

function readInt(m: Record<string, unknown>, camel: string, pascal: string): number {
  const v = m[camel] ?? m[pascal]
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v)
  if (typeof v === 'string' && v.trim()) return Number.parseInt(v, 10) || 0
  return 0
}

function readBool(m: Record<string, unknown>, camel: string, pascal: string): boolean {
  const v = m[camel] ?? m[pascal]
  if (typeof v === 'boolean') return v
  if (v === 'true' || v === 'True') return true
  if (v === 'false' || v === 'False') return false
  return false
}

function readNullableStr(v: unknown): string | null {
  if (v == null) return null
  const s = String(v).trim()
  return s.length > 0 ? s : null
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object') return null
  return v as Record<string, unknown>
}

export function parseInAppNotification(data: unknown): InAppNotification | null {
  const m = asRecord(data)
  if (!m) return null
  const id = readStr(m, 'id', 'Id')
  if (!id) return null
  return {
    id,
    title: readStr(m, 'title', 'Title'),
    body: readStr(m, 'body', 'Body'),
    type: readInt(m, 'type', 'Type'),
    category: readNullableStr(m.category ?? m.Category),
    referenceId: readNullableStr(m.referenceId ?? m.ReferenceId),
    referenceType: readNullableStr(m.referenceType ?? m.ReferenceType),
    actionUrl: readNullableStr(m.actionUrl ?? m.ActionUrl),
    isRead: readBool(m, 'isRead', 'IsRead'),
    createdAt: readStr(m, 'createdAt', 'CreatedAt'),
    channels: readInt(m, 'channels', 'Channels') || 1,
  }
}

export function parseInAppNotificationListResult(data: unknown): InAppNotificationListResult {
  const m = asRecord(data) ?? {}
  const raw = m.items ?? m.Items
  const items: InAppNotification[] = []
  if (Array.isArray(raw)) {
    for (const row of raw) {
      const item = parseInAppNotification(row)
      if (item) items.push(item)
    }
  }
  return {
    items,
    totalCount: readInt(m, 'totalCount', 'TotalCount'),
  }
}

export function parseUnreadNotificationCount(data: unknown): number {
  if (typeof data === 'number' && Number.isFinite(data)) return Math.trunc(data)
  if (typeof data === 'string' && data.trim()) return Number.parseInt(data, 10) || 0
  return 0
}

export function parseNotificationRule(data: unknown): NotificationRule | null {
  const m = asRecord(data)
  if (!m) return null
  const id = readStr(m, 'id', 'Id')
  if (!id) return null
  return {
    id,
    eventType: readInt(m, 'eventType', 'EventType'),
    audienceRole: readStr(m, 'audienceRole', 'AudienceRole'),
    isEnabled: (() => {
      const v = m.isEnabled ?? m.IsEnabled
      if (typeof v === 'boolean') return v
      return true
    })(),
    enabledChannels: readInt(m, 'enabledChannels', 'EnabledChannels') || 15,
    stageFilter: readNullableStr(m.stageFilter ?? m.StageFilter),
    clientFilter: readNullableStr(m.clientFilter ?? m.ClientFilter),
    quietHoursStart: readNullableStr(m.quietHoursStart ?? m.QuietHoursStart),
    quietHoursEnd: readNullableStr(m.quietHoursEnd ?? m.QuietHoursEnd),
    digestMode: readInt(m, 'digestMode', 'DigestMode'),
  }
}

export function parseNotificationRuleList(data: unknown): NotificationRule[] {
  if (!Array.isArray(data)) return []
  const out: NotificationRule[] = []
  for (const row of data) {
    const item = parseNotificationRule(row)
    if (item) out.push(item)
  }
  return out
}

export function buildNotificationRuleUpdateBody(rule: NotificationRule): Record<string, unknown> {
  return {
    isEnabled: rule.isEnabled,
    enabledChannels: rule.enabledChannels,
    stageFilter: rule.stageFilter,
    clientFilter: rule.clientFilter,
    quietHoursStart: rule.quietHoursStart,
    quietHoursEnd: rule.quietHoursEnd,
    digestMode: rule.digestMode,
    eventType: rule.eventType,
    audienceRole: rule.audienceRole,
  }
}

export function parseNotificationTemplate(data: unknown): NotificationTemplate | null {
  const m = asRecord(data)
  if (!m) return null
  const id = readStr(m, 'id', 'Id')
  if (!id) return null
  return {
    id,
    eventType: readInt(m, 'eventType', 'EventType'),
    audienceRole: readStr(m, 'audienceRole', 'AudienceRole'),
    channel: readInt(m, 'channel', 'Channel'),
    subjectTemplate: readNullableStr(m.subjectTemplate ?? m.SubjectTemplate),
    bodyTemplate: readStr(m, 'bodyTemplate', 'BodyTemplate'),
    isCustom: readBool(m, 'isCustom', 'IsCustom'),
  }
}

export function parseNotificationTemplateList(data: unknown): NotificationTemplate[] {
  if (!Array.isArray(data)) return []
  const out: NotificationTemplate[] = []
  for (const row of data) {
    const item = parseNotificationTemplate(row)
    if (item) out.push(item)
  }
  return out
}

export function parseNotificationTemplatePreview(data: unknown): NotificationTemplatePreview {
  const m = asRecord(data) ?? {}
  return {
    subject: readStr(m, 'subject', 'Subject'),
    body: readStr(m, 'body', 'Body'),
  }
}

export function parseUserNotificationPreference(data: unknown): UserNotificationPreference | null {
  const m = asRecord(data)
  if (!m) return null
  return {
    eventType: readInt(m, 'eventType', 'EventType'),
    mutedChannels: readInt(m, 'mutedChannels', 'MutedChannels'),
    tenantAllowedChannels: readInt(m, 'tenantAllowedChannels', 'TenantAllowedChannels') || 15,
    effectiveChannels: readInt(m, 'effectiveChannels', 'EffectiveChannels') || 15,
  }
}

export function parseUserNotificationPreferenceList(
  data: unknown,
): UserNotificationPreference[] {
  if (!Array.isArray(data)) return []
  const out: UserNotificationPreference[] = []
  for (const row of data) {
    const item = parseUserNotificationPreference(row)
    if (item) out.push(item)
  }
  return out
}
